import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Aba Voz do bloco de IA: "Voz natural (beta)". A IA responde com mensagem de
// voz — uma voz do catalogo da plataforma ou a gravacao do proprio cliente.
// Desligada por padrao; quando ligada, qualquer falha cai para texto no servidor.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const SEGUNDOS = 20;
const FRASE_TESTE = "Oi! Tudo bem? Aqui é do atendimento. Me conta como posso te ajudar hoje.";
const MINHA = "__minha__";

const ouvir = caminho => new Audio(`${BACKEND}/${String(caminho || "").replace(/^\/+/, "")}`).play();

const VozNatural = ({ valor, onChange, subir }) => {
  const v = valor || {};
  const [vozes, setVozes] = useState([]);
  const [gravando, setGravando] = useState(false);
  const [restante, setRestante] = useState(SEGUNDOS);
  const [enviando, setEnviando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [frase, setFrase] = useState(FRASE_TESTE);
  const gravador = useRef(null);
  const relogio = useRef(null);
  const arquivoRef = useRef(null);

  const muda = campos => onChange({ ativa: false, modo: "mesmoFormato", ...v, ...campos });
  const escolhida = v.vozId ? v.vozId : MINHA;
  const vozDoCatalogo = vozes.find(x => x.id === v.vozId);
  const temVoz = v.vozId ? !!vozDoCatalogo : !!v.amostra;

  useEffect(() => {
    api
      .get("/voz-natural/vozes")
      .then(({ data }) => setVozes(Array.isArray(data) ? data : []))
      .catch(() => setVozes([]));
    return () => clearInterval(relogio.current);
  }, []);

  const guardar = async arquivo => {
    setEnviando(true);
    try {
      const caminho = await subir(arquivo);
      if (caminho) {
        muda({ amostra: caminho, vozId: "" });
        toast.success("Voz guardada! Agora clique em Ouvir teste.");
      }
    } finally {
      setEnviando(false);
    }
  };

  const gravar = async () => {
    try {
      const fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
      const pedacos = [];
      const rec = new MediaRecorder(fluxo);
      rec.ondataavailable = e => e.data && e.data.size && pedacos.push(e.data);
      rec.onstop = () => {
        fluxo.getTracks().forEach(t => t.stop());
        clearInterval(relogio.current);
        setGravando(false);
        const blob = new Blob(pedacos, { type: rec.mimeType || "audio/webm" });
        const ext = (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
        guardar(new File([blob], `minha-voz-${Date.now()}.${ext}`, { type: blob.type }));
      };
      gravador.current = rec;
      rec.start();
      setGravando(true);
      setRestante(SEGUNDOS);
      relogio.current = setInterval(() => {
        setRestante(s => {
          if (s <= 1) {
            if (gravador.current && gravador.current.state === "recording") gravador.current.stop();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error("Não consegui usar o microfone. Libere o microfone no navegador ou envie um arquivo de áudio.");
    }
  };

  const parar = () => {
    if (gravador.current && gravador.current.state === "recording") gravador.current.stop();
  };

  const testar = async () => {
    setTestando(true);
    try {
      const { data } = await api.post("/voz-natural/teste", { texto: frase, amostra: v.amostra, vozId: v.vozId });
      await new Audio(data.url).play();
    } catch (err) {
      toastError(err);
    } finally {
      setTestando(false);
    }
  };

  return (
    <Paper style={{ padding: 16, marginBottom: 16, border: "1px solid #c7d2fe", backgroundColor: "#eef2ff" }}>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Voz natural</Typography>
        <Chip label="beta" size="small" color="primary" />
      </Box>
      <Typography variant="body2" color="textSecondary" style={{ margin: "4px 0 12px" }}>
        A IA responde com mensagem de voz numa voz de verdade: escolha uma das vozes prontas ou grave a sua. Respostas
        com link, chave Pix ou telefone continuam em texto, para o cliente poder copiar.
      </Typography>

      <FormControlLabel
        control={<Switch checked={!!v.ativa} onChange={e => muda({ ativa: e.target.checked })} color="primary" />}
        label={v.ativa ? "Ligada neste bloco" : "Desligada"}
      />

      <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 12 }}>
        <InputLabel>Qual voz</InputLabel>
        <Select
          label="Qual voz"
          value={escolhida}
          onChange={e => muda({ vozId: e.target.value === MINHA ? "" : e.target.value })}
        >
          <MenuItem value={MINHA}>Minha voz (gravar ou enviar um áudio)</MenuItem>
          {vozes.map(x => (
            <MenuItem key={x.id} value={x.id}>
              {x.nome}
              {x.descricao ? ` — ${x.descricao}` : ""}
            </MenuItem>
          ))}
          {v.vozId && !vozDoCatalogo && <MenuItem value={v.vozId}>Voz que não está mais disponível</MenuItem>}
        </Select>
      </FormControl>

      {v.vozId ? (
        vozDoCatalogo && (
          <Box style={{ marginTop: 8 }}>
            <Button size="small" startIcon={<PlayArrowIcon />} onClick={() => ouvir(vozDoCatalogo.amostra)}>
              Ouvir a voz "{vozDoCatalogo.nome}"
            </Button>
          </Box>
        )
      ) : (
        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginTop: 12 }}>
          {gravando ? (
            <Button variant="contained" color="secondary" startIcon={<StopIcon />} onClick={parar}>
              Parar ({restante}s)
            </Button>
          ) : (
            <Button variant="outlined" color="primary" startIcon={<MicIcon />} onClick={gravar} disabled={enviando}>
              {v.amostra ? "Gravar de novo" : "Gravar minha voz"}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => arquivoRef.current && arquivoRef.current.click()}
            disabled={gravando || enviando}
          >
            Enviar áudio
          </Button>
          <input
            ref={arquivoRef}
            type="file"
            accept="audio/*"
            style={{ display: "none" }}
            onChange={e => {
              const f = e.target.files && e.target.files[0];
              e.target.value = "";
              if (f) guardar(f);
            }}
          />
          {enviando && <Typography variant="caption">Guardando…</Typography>}
          {v.amostra && !enviando && (
            <Button size="small" startIcon={<PlayArrowIcon />} onClick={() => ouvir(v.amostra)}>
              Ouvir gravação
            </Button>
          )}
        </Box>
      )}

      <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 16 }}>
        <InputLabel>Quando responder em áudio</InputLabel>
        <Select
          label="Quando responder em áudio"
          value={v.modo || "mesmoFormato"}
          onChange={e => muda({ modo: e.target.value })}
        >
          <MenuItem value="mesmoFormato">No mesmo formato do cliente: áudio vira áudio, texto vira texto</MenuItem>
          <MenuItem value="sempre">Sempre em áudio</MenuItem>
        </Select>
      </FormControl>

      {temVoz && (
        <Box display="flex" style={{ gap: 8, marginTop: 16 }} alignItems="flex-start">
          <TextField
            label="Frase para testar"
            variant="outlined"
            size="small"
            fullWidth
            value={frase}
            onChange={e => setFrase(e.target.value)}
            inputProps={{ maxLength: 300 }}
          />
          <Button variant="contained" color="primary" onClick={testar} disabled={testando} style={{ whiteSpace: "nowrap" }}>
            {testando ? "Gerando…" : "Ouvir teste"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default VozNatural;
